import { useToast } from '@chakra-ui/react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { NfseBatchResponse } from 'services/interfaces/nfse';
import * as nfseService from 'services/nfse';
import { validateDocument } from './helpers/csvValidation';
import { NfseBatch, NfseBatchRecord } from './types/nfse.types';

interface NfseContextData {
  batches: NfseBatch[];
  loading: boolean;
  error: string | null;
  selectedBatch: NfseBatch | null;
  loadBatches: () => Promise<void>;
  loadBatch: (id: number) => Promise<void>;
  clearSelectedBatch: () => void;
}

const Context = createContext<NfseContextData>({} as NfseContextData);

export const useNfseContext = () => useContext(Context);

function mapBatchResponseToBatch(response: NfseBatchResponse): NfseBatch {
  return {
    id: response.id,
    quantity: response.quantity,
    totalValue:
      typeof response.totalValue === 'string'
        ? parseFloat(response.totalValue)
        : response.totalValue,
    referenceDate: response.referenceDate,
    status: response.status,
    createdAt: response.createdAt,
    records: (response.records || []).map(
      (record): NfseBatchRecord => ({
        id: record.id,
        document: record.document,
        documentType: validateDocument(record.document) || 'CPF',
        value:
          typeof record.value === 'string'
            ? parseFloat(record.value)
            : record.value,
        customerId: record.customerId,
        status: record.status,
      }),
    ),
  };
}

interface NfseProviderProps {
  children: React.ReactNode;
}

export const NfseProvider: React.FC<NfseProviderProps> = ({ children }) => {
  const toast = useToast();
  const [batches, setBatches] = useState<NfseBatch[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedBatch, setSelectedBatch] = useState<NfseBatch | null>(null);

  const loadBatches = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await nfseService.listBatches();
      const mappedBatches = data.map(mapBatchResponseToBatch);
      setBatches(mappedBatches);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Erro ao carregar lotes';
      setError(message);
      toast({
        title: 'Erro',
        description: message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const loadBatch = useCallback(
    async (id: number) => {
      setLoading(true);
      setError(null);
      try {
        const response = await nfseService.getBatch(id);
        const mappedBatch = mapBatchResponseToBatch(response.data);
        setSelectedBatch(mappedBatch);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Erro ao carregar lote';
        setError(message);
        toast({
          title: 'Erro',
          description: message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    },
    [toast],
  );

  const clearSelectedBatch = useCallback(() => {
    setSelectedBatch(null);
  }, []);

  useEffect(() => {
    loadBatches();
  }, [loadBatches]);

  const contextValue: NfseContextData = {
    batches,
    loading,
    error,
    selectedBatch,
    loadBatches,
    loadBatch,
    clearSelectedBatch,
  };

  return <Context.Provider value={contextValue}>{children}</Context.Provider>;
};

export default NfseProvider;
